import React, { useState, useEffect } from 'react';
import { FAQQuestion, FAQAnswer } from './FAQPage';
import { supabase } from '../../lib/supabase';
import { useUserStore } from '../../lib/userStore';
import toast from 'react-hot-toast';

interface QuestionDetailModalProps {
  question: FAQQuestion;
  show: boolean;
  onHide: () => void;
  onQuestionUpdated: () => void;
  categories: Array<{ value: string; label: string }>;
  priorities: Array<{ value: string; label: string; color: string }>;
}

export function QuestionDetailModal({ 
  question, 
  show, 
  onHide, 
  onQuestionUpdated,
  categories,
  priorities 
}: QuestionDetailModalProps) {
  const { user_id } = useUserStore();
  const [answers, setAnswers] = useState<FAQAnswer[]>([]);
  const [newAnswer, setNewAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [isCommissioner, setIsCommissioner] = useState(false);

  useEffect(() => {
    if (show && question) {
      loadAnswers();
      incrementViewCount();
      checkCommissionerStatus();
    }
  }, [show, question]);

  const loadAnswers = async () => {
    try {
      setLoading(true);
      
      // Requête directe avec JOIN
      const { data, error } = await supabase
        .from('faq_answers')
        .select(`
          *,
          users!faq_answers_user_id_fkey(username)
        `)
        .eq('question_id', question.id)
        .order('is_official', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Transformer les données
      const transformedData = (data || []).map((item: any) => ({
        id: item.id,
        question_id: item.question_id,
        user_id: item.user_id,
        username: item.users?.username || 'Utilisateur inconnu',
        answer: item.answer,
        is_official: item.is_official,
        created_at: item.created_at,
        updated_at: item.updated_at
      }));

      setAnswers(transformedData);
    } catch (error) {
      console.error('Error loading answers:', error);
      toast.error('Erreur lors du chargement des réponses');
    } finally {
      setLoading(false);
    }
  };

  const incrementViewCount = async () => {
    try {
      await supabase
        .from('faq_questions')
        .update({ views_count: question.views_count + 1 })
        .eq('id', question.id);
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const checkCommissionerStatus = async () => {
    try {
      const { data } = await supabase
        .from('leagues_users')
        .select('is_commissioner')
        .eq('league_id', question.league_id)
        .eq('user_id', user_id)
        .single();
      
      setIsCommissioner(data?.is_commissioner || false);
    } catch (error) {
      console.error('Error checking commissioner status:', error);
    }
  };

  const handleSubmitAnswer = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newAnswer.trim()) {
      toast.error('Veuillez saisir votre réponse');
      return;
    }

    if (newAnswer.length < 10) {
      toast.error('La réponse doit contenir au moins 10 caractères');
      return;
    }

    try {
      setSubmittingAnswer(true);
      
      // Insertion directe dans la table
      const { error } = await supabase
        .from('faq_answers')
        .insert({
          question_id: question.id,
          user_id: user_id,
          answer: newAnswer.trim(),
          is_official: isCommissioner
        });

      if (error) throw error;

      // Mettre à jour le statut de la question si c'est une réponse officielle
      if (isCommissioner) {
        await supabase
          .from('faq_questions')
          .update({ 
            status: 'answered',
            updated_at: new Date().toISOString()
          })
          .eq('id', question.id);
      }

      setNewAnswer('');
      loadAnswers();
      onQuestionUpdated();
      toast.success('Réponse ajoutée avec succès!');
    } catch (error: any) {
      console.error('Error creating answer:', error);
      toast.error(error.message || 'Erreur lors de la création de la réponse');
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const handleUpdateStatus = async (status: string, isPinned?: boolean) => {
    try {
      const updateData: any = { 
        status: status,
        updated_at: new Date().toISOString()
      };
      
      if (isPinned !== undefined) {
        updateData.is_pinned = isPinned;
      }

      const { error } = await supabase
        .from('faq_questions')
        .update(updateData)
        .eq('id', question.id);

      if (error) throw error;

      onQuestionUpdated();
      toast.success('Question mise à jour avec succès!');
    } catch (error: any) {
      console.error('Error updating question:', error);
      toast.error(error.message || 'Erreur lors de la mise à jour');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'primary';
      case 'answered': return 'success';
      case 'closed': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open': return 'Ouverte';
      case 'answered': return 'Répondue';
      case 'closed': return 'Fermée';
      default: return status;
    }
  };

  const getPriorityData = (priority: string) => {
    return priorities.find(p => p.value === priority) || priorities[0];
  };

  const getCategoryLabel = (category: string) => {
    const cat = categories.find(c => c.value === category);
    return cat?.label || category;
  };

  if (!show) return null;

  const priorityData = getPriorityData(question.priority);

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-question-circle me-2"></i>
              Détails de la question
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onHide}
            ></button>
          </div>
          
          <div className="modal-body">
            {/* Question Header */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {question.is_pinned && (
                      <span className="badge bg-warning text-dark">
                        <i className="fas fa-thumbtack me-1"></i>
                        Épinglée
                      </span>
                    )}
                    <span className={`badge bg-${getStatusColor(question.status)}`}>
                      {getStatusLabel(question.status)}
                    </span>
                    <span className={`badge bg-${priorityData.color}`}>
                      {priorityData.label}
                    </span>
                    <span className="badge bg-info">
                      {getCategoryLabel(question.category)}
                    </span>
                  </div>
                  <div className="text-muted small">
                    <i className="fas fa-eye me-1"></i>
                    {question.views_count + 1} vues
                  </div>
                </div>

                <h4 className="mb-3">{question.question}</h4>

                <div className="d-flex justify-content-between align-items-center text-muted small">
                  <div>
                    <i className="fas fa-user me-1"></i>
                    Posée par <strong>{question.username}</strong>
                  </div>
                  <div>
                    <i className="fas fa-clock me-1"></i>
                    {formatDate(question.created_at)}
                  </div>
                </div>

                {/* Commissioner Actions */}
                {isCommissioner && (
                  <div className="mt-3 pt-3 border-top">
                    <h6>Actions du commissaire :</h6>
                    <div className="d-flex gap-2 flex-wrap">
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => handleUpdateStatus('answered')}
                        disabled={question.status === 'answered'}
                      >
                        <i className="fas fa-check me-1"></i>
                        Marquer comme répondue
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleUpdateStatus('closed')}
                        disabled={question.status === 'closed'}
                      >
                        <i className="fas fa-times me-1"></i>
                        Fermer
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleUpdateStatus('open')}
                        disabled={question.status === 'open'}
                      >
                        <i className="fas fa-unlock me-1"></i>
                        Rouvrir
                      </button>
                      <button
                        className={`btn btn-sm ${question.is_pinned ? 'btn-warning' : 'btn-outline-warning'}`}
                        onClick={() => handleUpdateStatus(question.status, !question.is_pinned)}
                      >
                        <i className="fas fa-thumbtack me-1"></i>
                        {question.is_pinned ? 'Désépingler' : 'Épingler'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Answers */}
            <div className="mb-4">
              <h5 className="mb-3">
                <i className="fas fa-comments me-2"></i>
                Réponses ({answers.length})
              </h5>
              
              {loading ? (
                <div className="text-center py-3">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                </div>
              ) : answers.length > 0 ? (
                <div className="space-y-3">
                  {answers.map(answer => (
                    <div 
                      key={answer.id} 
                      className={`card ${answer.is_official ? 'border-success' : ''}`}
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <strong>{answer.username}</strong>
                            {answer.is_official && (
                              <span className="badge bg-success">
                                <i className="fas fa-medal me-1"></i>
                                Réponse officielle
                              </span>
                            )}
                          </div>
                          <small className="text-muted">
                            {formatDate(answer.created_at)}
                          </small>
                        </div>
                        <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                          {answer.answer}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-comment-slash fa-2x mb-2"></i>
                  <p>Aucune réponse pour le moment</p>
                </div>
              )}
            </div>

            {/* Add Answer Form */}
            {question.status !== 'closed' && (
              <div className="card">
                <div className="card-body">
                  <h6 className="mb-3">
                    <i className="fas fa-reply me-2"></i>
                    {isCommissioner ? 'Ajouter une réponse officielle' : 'Ajouter un commentaire'}
                  </h6>
                  
                  <form onSubmit={handleSubmitAnswer}>
                    <div className="mb-3">
                      <textarea
                        className="form-control"
                        rows={4}
                        value={newAnswer}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewAnswer(e.target.value)}
                        placeholder={isCommissioner ? "Rédigez votre réponse officielle..." : "Rédigez votre commentaire..."}
                        disabled={submittingAnswer}
                        maxLength={2000}
                      />
                      <div className="form-text">
                        {newAnswer.length}/2000 caractères (minimum 10)
                      </div>
                    </div>
                    
                    <button
                      type="submit"
                      className={`btn ${isCommissioner ? 'btn-success' : 'btn-primary'}`}
                      disabled={submittingAnswer || !newAnswer.trim() || newAnswer.length < 10}
                    >
                      {submittingAnswer ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Envoi...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-2"></i>
                          {isCommissioner ? 'Publier la réponse' : 'Publier le commentaire'}
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button 
              type="button"
              className="btn btn-secondary"
              onClick={onHide}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
